import React, { useState } from 'react';
import { ArrowRight, ArrowRightLeft, CalendarRange, Camera } from 'lucide-react';
import type {
  PeriodFigures,
  PeriodKey,
  WindowFigures,
  WindowSelection,
} from '../../../hooks/pipelineTracker/usePipelineReport';
import { EARLIEST_WINDOW_START } from '../../../hooks/pipelineTracker/reportContract';
import { OWNER_EVOKE, OWNER_SALESTREKKER } from '../../../hooks/pipelineTracker/operationsContract';
import { prettyDate } from './operationsParts';
import {
  CHART_PALETTE, Chip, HousePanel, HouseStatCard, HouseStatGrid, PeriodBar, type ChipTone, type PeriodPreset,
} from './trackerKit';
import { money, num, type ComingSoonItem } from './pipelineParts';
import {
  NO_TARGET_SET,
  RAG_LABEL,
  attainment,
  hasAnyTarget,
  stageForFlow,
  windowCountTarget,
  windowValueTargetCents,
  type RagTone,
  type TargetScope,
} from '../../../hooks/pipelineTracker/targetsContract';

/**
 * WTD / MTD / QTD / FYTD / CYTD, and what happened inside the chosen window.
 *
 * Two components, one window. `PeriodControl` is the choice, and sits in the scope row at the top
 * of the tab with the broker chips and the filters, because scope is the first decision a reader
 * makes (Evoke, 25 Sep 2026). `PeriodBand` is the deal movements inside the chosen window, and
 * states that window in words and dates beside the figures it describes.
 *
 * STOCK VERSUS FLOW is the whole design. Everything below the movements is a photograph of one
 * instant: deals in flight, their stages, their ages. No period can filter a photograph, because a
 * deal in flight is in flight regardless of which month you ask about. What a period CAN measure
 * is what happened between two dates, so the band shows the events the export actually dates:
 * created, lodged, approved, settled.
 *
 * THE WINDOW ENDS AT THE SNAPSHOT, NOT TODAY. Opening a 29 August export on 2 September must not
 * show a month running to 2 September while every deal in the file stopped on 29 August. The dates
 * are printed for exactly this reason.
 *
 * CUSTOM DATES sit at the end of the same control, because the question a tenant asked ("missing
 * custom dates as an option?") is the same question the five buttons answer, over a window they
 * choose. They are applied on a button rather than on each keystroke: a half-typed year is a valid
 * date the server would answer honestly and uselessly, and the answer costs a round trip. The same
 * From/To/Apply shape as the Leads & Partners tab, so the two surfaces do not teach two habits for
 * one idea.
 */

const ORDER: PeriodKey[] = ['wtd', 'mtd', 'qtd', 'fytd', 'cytd'];

const SHORT: Record<PeriodKey, string> = {
  wtd: 'WTD',
  mtd: 'MTD',
  qtd: 'QTD',
  fytd: 'FYTD',
  cytd: 'CYTD',
};

const LONG: Record<PeriodKey, string> = {
  wtd: 'week to date',
  mtd: 'month to date',
  qtd: 'financial quarter to date',
  fytd: 'financial year to date',
  cytd: 'calendar year to date',
};

type DatedFlow = 'created' | 'lodged' | 'approved' | 'settled';

/**
 * The movements row, in pipeline order, in Evoke's own words (agreed 25 Sep 2026): the four stages
 * the export dates, on one row.
 *
 * Meetings, Analysis and Proposals are stages the export does not date, so no window can count
 * them from one file. They are not drawn as tiles (three "Building" tiles made the row read as
 * empty; founder, 1 Oct 2026). They are named once in the Pipeline tab's "Coming when the data is
 * available" list (UNDATED_STAGES_ITEM), so a reader still learns why they are absent.
 *
 * Lost is not in this row, by decision (Evoke and the founder, 25 and 28 Sep 2026). The windowed
 * "lost in the period" count is therefore no longer shown on this tab; what remains is the
 * snapshot's losses by stage ("Where deals were lost") and the cohort band's losses among deals
 * created in the window. `periods[].lost` stays in the payload.
 */
export const MOVEMENTS: ReadonlyArray<{ key: DatedFlow; label: string; note: string; colour: string }> = [
  { key: 'created', label: 'Leads', note: 'deals created', colour: CHART_PALETTE[0] },
  { key: 'lodged', label: 'Submissions', note: 'lodged with a lender', colour: CHART_PALETTE[1] },
  { key: 'approved', label: 'Approvals', note: 'approved', colour: CHART_PALETTE[3] },
  { key: 'settled', label: 'Settlements', note: 'settled', colour: CHART_PALETTE[2] },
];

/** Approval %, the fifth tile: cyan, so it never reads as a fifth dated stage. */
const APPROVAL_COLOUR = CHART_PALETTE[5];

/** The three undated stages, as one entry in the Pipeline tab's "Coming when the data is available" list. */
export const UNDATED_STAGES_ITEM: ComingSoonItem = {
  section: 'Meetings, Analysis and Proposals',
  needs: 'The date a deal enters each stage, or enough weekly uploads to see deals move between them.',
  from: 'Salestrekker',
};

/**
 * Approval % for the window on screen, the fifth tile of the movements row (V6; it was the
 * "Compliance and risk snapshot" card above the row until the founder merged the two, 5 Oct 2026).
 *
 * APPROVAL % IS A FLOW RATIO, NOT A COHORT, and the tile says so. It divides the deals whose
 * approval date falls inside the selected window by the deals whose lodged date falls inside the
 * same window: exactly the Approvals and Submissions tiles beside it. An approval in the window can
 * belong to a deal lodged before it, so the ratio can pass 100% in a quiet month after a busy one.
 * A cohort rate (of the deals lodged, how many were approved) is the stage-to-stage conversion
 * that repeated weekly uploads will support.
 */
export interface ApprovalRate {
  approved: number;
  lodged: number;
  /** Whole percent, or null when nothing was lodged in the window. */
  pct: number | null;
}

export function approvalRate(window: Pick<WindowFigures, 'approved' | 'lodged'>): ApprovalRate {
  const approved = window.approved.count;
  const lodged = window.lodged.count;
  return {
    approved,
    lodged,
    pct: lodged > 0 ? Math.round((approved / lodged) * 100) : null,
  };
}

/**
 * The three compliance metrics that cannot be computed yet, as entries in the Pipeline tab's
 * "Coming when the data is available" list. Each would be an invention today, because the exports
 * carry no pass threshold and no request or return timestamps (founder decision, 28 Sep 2026).
 */
export const COMPLIANCE_SNAPSHOT_WAITING: ReadonlyArray<ComingSoonItem> = [
  {
    section: 'Compliance %',
    from: OWNER_EVOKE,
    needs: 'Pass thresholds for file quality. Operations > File quality applies no pass or fail until they are set.',
  },
  {
    section: 'DIP SLA',
    from: OWNER_SALESTREKKER,
    needs: 'Request and return timestamps on every deal type.',
  },
  {
    section: 'MIR SLA',
    from: OWNER_SALESTREKKER,
    needs: 'Request and return timestamps on every deal type.',
  },
];

const fmtDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/**
 * The window on screen: the applied custom one, the selected rolling one, or the first.
 *
 * Exported so the Pipeline tab's other windowed sections (Deal mix, Velocity, Forecast, Planned)
 * follow Deal movements by calling THIS function rather than restating it: while Custom is chosen
 * but not applied, every one of them shows the same fallback window, labelled the same way.
 * `period` is 'custom' only when the applied custom window is the one shown.
 */
export const currentWindow = (
  periods: readonly PeriodFigures[],
  selected: WindowSelection,
  custom?: WindowFigures,
): { window: WindowFigures; label: string; period: PeriodKey | 'custom' } => {
  const showCustom = selected === 'custom';
  const window: WindowFigures =
    (showCustom ? custom : periods.find((p) => p.period === selected)) ?? periods[0];
  if (showCustom && custom) return { window, label: 'custom dates', period: 'custom' };
  const period = (window as PeriodFigures).period;
  return { window, label: LONG[period], period };
};

/** '1 Jul', or '1 Jul 2025' when the window crosses a year, so a chip never hides which year. */
const chipDay = (iso: string, withYear: boolean): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
  });
};

/** "FYTD · 1 Jul to 12 Sept". Exported for the tests. */
export const periodChipText = (period: PeriodKey | 'custom', from: string, to: string): string => {
  const crossesYear = from.slice(0, 4) !== to.slice(0, 4);
  return `${period === 'custom' ? 'Custom' : SHORT[period]} \u00b7 ${chipDay(from, crossesYear)} to ${chipDay(to, crossesYear)}`;
};

/**
 * The scope chips (founder, 5 Oct 2026: "data isn't changing from WTD to FYTD", read off a section
 * the period never changes). Every panel and group says which of two things drives it: the period
 * chosen above (PeriodChip, brand blue, the colour of the selector itself) or the snapshot date
 * (AsAtChip, muted), so nobody waits for a photograph to move.
 */
export const PeriodChip: React.FC<{ period: PeriodKey | 'custom'; from: string; to: string; tone?: ChipTone }> = ({
  period, from, to, tone = 'blue',
}) => (
  <span data-testid="period-chip" title="Changes with the period selected above">
    <Chip tone={tone}>
      <CalendarRange className="h-3 w-3" aria-hidden="true" />
      {periodChipText(period, from, to)}
    </Chip>
  </span>
);

/** "As at 12 Sept 2026": a figure the period selector does not change. */
export const AsAtChip: React.FC<{ asAtDate: string; prefix?: string }> = ({ asAtDate, prefix = 'As at' }) => (
  <span data-testid="as-at-chip" title="A photograph of the snapshot date. The period does not change it.">
    <Chip tone="muted">
      <Camera className="h-3 w-3" aria-hidden="true" />
      {prefix} {fmtDate(asAtDate)}
    </Chip>
  </span>
);

/** "12 months to Sept 2026", for a trend that ignores the period. */
export const TrailingChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span data-testid="trailing-chip" title="A fixed run of months. The period does not change it.">
    <Chip tone="muted">
      <CalendarRange className="h-3 w-3" aria-hidden="true" />
      {children}
    </Chip>
  </span>
);

export interface PeriodControlProps {
  periods: PeriodFigures[];
  selected: WindowSelection;
  onSelect: (selection: WindowSelection) => void;
  /** The applied custom window, once the server has answered for one. */
  custom?: WindowFigures;
  /** Apply a custom window. Both dates, inclusive; the server checks them again. */
  onApplyCustom?: (from: string, to: string) => void;
}

/** The house PeriodBar presets for the windows a report returned, in WTD..CYTD order. */
export const periodPresets = (periods: readonly PeriodFigures[]): PeriodPreset[] =>
  ORDER.filter((k) => periods.some((p) => p.period === k)).map((k) => ({ key: k, label: SHORT[k] }));

/**
 * The period choice for a pipeline report, rendered through the house PeriodBar (trackerKit) with
 * no label of its own. Bounds a custom window to the snapshot (the export cannot answer for a day it
 * does not contain) and to EARLIEST_WINDOW_START, the floor that stops a mistyped year.
 */
export const PeriodControl: React.FC<PeriodControlProps & { label?: string | null }> = ({
  periods,
  selected,
  onSelect,
  custom,
  onApplyCustom,
  label = null,
}) => {
  if (periods.length === 0) return null;
  return (
    <PeriodBar
      presets={periodPresets(periods)}
      selected={selected}
      onSelect={(key) => onSelect(key as WindowSelection)}
      custom={custom}
      onApplyCustom={onApplyCustom}
      minDate={EARLIEST_WINDOW_START}
      maxDate={periods[0].to}
      label={label}
    />
  );
};

export interface PeriodBandProps {
  periods: PeriodFigures[];
  selected: WindowSelection;
  /** The applied custom window, once the server has answered for one. */
  custom?: WindowFigures;
  /**
   * The snapshot date. The pipeline figures below the band are a photograph of this day, which
   * a custom window ending earlier must not be mistaken for.
   */
  asAtDate?: string;
  /**
   * The targets that apply to the view on screen, already resolved for the broker / book / filter
   * scope by resolveTargetScope(). Omitted renders the band exactly as it was before targets
   * existed. A tile with no target prints no target line at all; a scope of kind 'none' grades
   * nothing and states its reason ONCE under the row, never on every tile.
   */
  targets?: TargetScope;
  /**
   * One quiet line under the row, from the tab: the prompt to set targets when none are set
   * (administrators only). Omitted renders nothing.
   */
  targetsNote?: React.ReactNode;
  /** The panel's accent: its group's colour on the jump bar. Brand blue by default. */
  accent?: string;
}

type Mode = '$' | '#';

const RAG_RING: Record<RagTone, string> = {
  green: 'ring-2 ring-green-500/70',
  amber: 'ring-2 ring-amber-500/70',
  red: 'ring-2 ring-red-500/70',
};

const RAG_TEXT: Record<RagTone, string> = {
  green: 'text-green-300',
  amber: 'text-amber-300',
  red: 'text-red-300',
};

/**
 * One tile's target line and grade, in the unit the toggle shows, or null for no line at all.
 *
 * A stage with no target prints nothing: "No target set" on every tile read as a row of blanks
 * (founder, 1 Oct 2026). A target only in the other unit does say so ("No count target set")
 * rather than grading dollars against a count: Evoke's settlement target is in dollars, and a #
 * view graded against it would be a number compared with a different kind of number.
 */
function tileTarget(
  flow: DatedFlow,
  figure: { count: number; valueCents: number },
  window: { from: string; to: string },
  mode: Mode,
  targets: TargetScope,
): { text: string; tone?: RagTone; pct?: number } | null {
  if (targets.kind === 'none') return null;
  const stage = targets.stages[stageForFlow(flow)];
  const hasCount = stage?.monthlyCount !== undefined;
  const hasValue = stage?.monthlyValueCents !== undefined;
  if (!hasCount && !hasValue) return null;

  const target =
    mode === '$'
      ? windowValueTargetCents(stage?.monthlyValueCents, window.from, window.to)
      : windowCountTarget(stage?.monthlyCount, window.from, window.to);
  if (target === null) return { text: mode === '$' ? 'No $ target set' : 'No count target set' };

  const shown = mode === '$' ? money(target) : num(target);
  const grade = attainment(mode === '$' ? figure.valueCents : figure.count, target, targets.rag);
  if (grade === null) return { text: `Target ${shown}` };
  return { text: `Target ${shown} \u00b7 ${grade.pct}%`, tone: grade.tone, pct: grade.pct };
}

const PeriodBand: React.FC<PeriodBandProps> = ({ periods, selected, custom, asAtDate, targets, targetsNote, accent }) => {
  // Dollars first, matching how the practice reads its own month (Evoke's mock, agreed 25 Sep).
  // One toggle for the whole row: a row where each tile chose its own unit could not be read across.
  const [mode, setMode] = useState<Mode>('$');

  if (periods.length === 0) return null;

  const { window: current, label: currentLabel, period: currentPeriod } = currentWindow(periods, selected, custom);
  const rate = approvalRate(current);

  return (
    <HousePanel
      title="Deal movements"
      icon={ArrowRightLeft}
      color={accent}
      badge={<PeriodChip period={currentPeriod} from={current.from} to={current.to} />}
      actions={
        <div
          className="inline-flex rounded-lg border border-gray-700 bg-gray-900/60 p-0.5"
          role="group"
          aria-label="Show deal movements as"
        >
          {(['$', '#'] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={m === mode}
              aria-label={m === '$' ? 'Dollars' : 'Deal counts'}
              onClick={() => setMode(m)}
              className={`rounded-md px-2.5 py-1 text-[0.6875rem] font-bold transition-colors ${
                m === mode ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      }
      blurb={
        // The window, spelled out. A label alone ("MTD") does not tell a reader whether the month
        // ran to the snapshot or to today, and those differ by however long ago the export was taken.
        <>
          Recorded dates in the export, {currentLabel}
          <span className="mx-2 text-gray-600">&middot;</span>
          <span className="tabular-nums text-gray-300">{fmtDate(current.from)}</span>
          <ArrowRight className="mx-1.5 inline h-3 w-3 text-gray-600" />
          <span className="tabular-nums text-gray-300">{fmtDate(current.to)}</span>
        </>
      }
    >
      <p className="mb-3 text-xs text-amber-300">
        Submissions and approvals depend on recorded milestone dates, which can be missing for
        commercial deals. They are not the total stage milestones reached. See Trends for
        observed milestones between snapshots.
      </p>

      <HouseStatGrid count={5} testId="deal-movements-row">
        {MOVEMENTS.map((movement) => {
          const figure = current[movement.key];
          const target = targets === undefined ? null : tileTarget(movement.key, figure, current, mode, targets);
          return (
            // The grade rings the card; colour is never the only signal (the grade is also a word).
            <div
              key={movement.key}
              data-rag={target?.tone ?? 'none'}
              className={`rounded-lg ${target?.tone ? RAG_RING[target.tone] : ''}`}
            >
              <HouseStatCard
                label={movement.label}
                color={movement.colour}
                valueColor={movement.colour}
                value={<span className="tabular-nums">{mode === '$' ? money(figure.valueCents) : num(figure.count)}</span>}
                sub={mode === '$' ? `${num(figure.count)} ${figure.count === 1 ? 'deal' : 'deals'}` : money(figure.valueCents)}
                testId={`movement-${movement.key}`}
                footer={
                  <>
                    <span className="text-[0.6875rem] text-gray-500">{movement.note}</span>
                    {target !== null && (
                      // Colour is never the only signal: the grade is also a word, beside the figure
                      // it was graded on (mockup V5, "colour is always paired with a label and a figure").
                      <span className="mt-1.5 block border-t border-gray-700/60 pt-1.5 text-[0.6875rem] tabular-nums leading-snug">
                        <span className={target.tone ? RAG_TEXT[target.tone] : 'text-gray-500'}>{target.text}</span>
                        {target.tone && (
                          <span className={`ml-1 font-semibold ${RAG_TEXT[target.tone]}`}>{RAG_LABEL[target.tone]}</span>
                        )}
                      </span>
                    )}
                  </>
                }
              />
            </div>
          );
        })}
        {/* Approval %, the same window's approvals over its lodgements. A percentage, so the $ / #
            toggle does not apply to it, and it carries no target, so no data-rag grade either. */}
        <HouseStatCard
          testId="approval-rate-tile"
          label="Approval %"
          color={APPROVAL_COLOUR}
          valueColor={APPROVAL_COLOUR}
          value={<span className="tabular-nums">{rate.pct === null ? '-' : `${rate.pct}%`}</span>}
          footer={
            <span className="text-[0.6875rem] leading-snug text-gray-500">
              {rate.pct === null
                ? `No deal was lodged ${prettyDate(current.from)} to ${prettyDate(current.to)}, so there is no rate.`
                : `${num(rate.approved)} approved over ${num(rate.lodged)} lodged, both dated ${prettyDate(current.from)} to ${prettyDate(current.to)}. Not a cohort: an approval may be for a deal lodged earlier.`}
            </span>
          }
        />
      </HouseStatGrid>

      <p className="mt-3 text-xs text-gray-400">
        Compliance %, DIP SLA and MIR SLA are listed at the foot of this tab until their inputs arrive.
      </p>

      {/* Why nothing is graded, said once for the row. "No target set" is the tab's to say, as a
          prompt to administrators (targetsNote), so it is not repeated here. */}
      {targets !== undefined && targets.kind === 'none' && targets.message !== NO_TARGET_SET && (
        <p className="mt-3 text-xs text-gray-400" data-testid="targets-scope-note">
          {targets.message}, so nothing on this row is graded.
        </p>
      )}

      {targetsNote}

      {targets !== undefined && targets.kind !== 'none' && hasAnyTarget(targets.stages) && (
        <p className="mt-3 text-xs leading-relaxed text-gray-400">
          {targets.kind === 'broker' ? "This broker's own targets. " : ''}
          Window target: each month&apos;s target in full for whole months, pro rata by days for part
          months. Green at {targets.rag.greenAtPct}% or more, amber {targets.rag.amberAtPct}% to{' '}
          {Math.max(targets.rag.amberAtPct, targets.rag.greenAtPct - 1)}%, red under {targets.rag.amberAtPct}%.
        </p>
      )}

      <p className="mt-3 text-xs leading-relaxed text-gray-500">
        Events dated inside this window, both dates included, counted from the same export. These are recorded events, not a complete history of stage movements or the
        live pipeline: the pipeline figures below are a photograph of {fmtDate(asAtDate ?? periods[0].to)} and no period
        narrows them, because a deal in flight is in flight whichever month you ask about. A deal
        that was created, lodged and settled inside the window appears in all three.
      </p>
    </HousePanel>
  );
};

export default PeriodBand;
